/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, TablePaginationConfig, TableProps } from 'antd';
import TableComponent from '../table';
import {
  IProduct,
  ISearchProductReq,
  ISearchProductRes,
} from '../../interfaces/product';
import { useMutation } from '@tanstack/react-query';
import { searchProducts } from '../../adapters/product';
import {
  FilterValue,
  SorterResult,
  TableRowSelection,
} from 'antd/es/table/interface';
import Search from 'antd/es/input/Search';

interface IProps {
  open: boolean;
  handleOk: (items: IProduct[]) => void;
  handleCancel: () => void;
  isMultiSelect?: boolean;
}

const _criteria: ISearchProductReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const ProductSelectorModal = ({
  open,
  handleOk,
  handleCancel,
  isMultiSelect = false,
}: IProps) => {
  const [criteria, setCriteria] = useState<ISearchProductReq>({
    ..._criteria,
  });
  const [product, setProduct] = useState<ISearchProductRes>({
    data: [],
    totalRecords: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([]);
  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    rows: IProduct[],
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedProducts(rows);
  };

  const rowSelection: TableRowSelection<IProduct> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const mutations = useMutation({
    mutationFn: async () => await searchProducts(criteria),
    mutationKey: ['fetchProduct'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        setProduct(data.data);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const fetchProduct = async () => {
    try {
      mutations.mutateAsync();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (criteria) {
      fetchProduct();
    }
  }, [criteria]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IProduct> | SorterResult<IProduct>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IProduct) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const columns: TableProps<IProduct>['columns'] = [
    {
      sorter: true,
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 120,
    },
    {
      sorter: true,
      title: 'สินค้า',
      dataIndex: 'name',
      key: 'name',
      className: 'font-bold',
    },
    {
      sorter: true,
      title: 'ราคาขาย',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      className: 'font-bold text-blue-600',
      align: 'right',
      render: (_, rec) => (
        <Space>
          {rec?.unitPrice?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Space>
      ),
    },
    {
      title: 'หน่วย Package',
      dataIndex: 'packageUnitName',
      key: 'packageUnitName',
      width: 120,
    },
    {
      title: 'Conversion',
      dataIndex: 'conversion',
      key: 'conversion',
      className: 'font-bold',
      align: 'right',
      width: 120,
      render: _ => (
        <Space>
          {(_ || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Space>
      ),
    },
    {
      title: 'หน่วยขาย',
      dataIndex: 'baseUnitName',
      key: 'baseUnitName',
      width: 120,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      className: 'font-medium',
      align: 'center',
      fixed: 'right',
      width: 60,
      hidden: isMultiSelect,
      render: (_, rec) => (
        <Button type="primary" size="small" onClick={() => handleOk([rec])}>
          เลือก
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="เลือกสินค้า"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={
        isMultiSelect && [
          <Button key="back" onClick={handleCancel}>
            ปิด
          </Button>,
          <Button
            disabled={!selectedRowKeys.length}
            key="submit"
            type="primary"
            form="unit"
            onClick={() => handleOk(selectedProducts)}
            className=" !hover:"
          >
            {`เลือก ${selectedRowKeys.length} รายการ`}
          </Button>,
        ]
      }
    >
      <div className="flex flex-col gap-2 w-full">
        <Search
          className="md:max-w-[400px] md:min-w-[400px]"
          placeholder="ค้นหา"
          allowClear
          size="large"
          enterButton="ค้นหา"
          onSearch={onSearch}
        />
        <TableComponent<IProduct>
          columns={columns}
          criteria={criteria}
          data={product}
          loading={mutations?.isPending}
          onTableChange={onTableChange}
          rowSelection={isMultiSelect ? rowSelection : undefined}
          scrollLength={900}
        />
      </div>
    </Modal>
  );
};

export default ProductSelectorModal;
